import { Badge, Surface } from '@chopchop-se/ui';
import type { StockMovementRow } from '@warehouse/shared';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDateTime, formatQty } from '../../../lib/format';

/**
 * The "Historik" list shared by the pallet and lot detail pages.
 *
 * It reads the ledger, not an activity feed: each row states what moved, from
 * where to where, how much, and who did it — which is exactly what somebody
 * reconstructing a batch recall needs, in the order they need it.
 */
export function MovementHistory({ rows }: { rows: readonly StockMovementRow[] }): ReactNode {
  const { t } = useTranslation('warehouseCore');

  if (rows.length === 0) {
    return (
      <Surface variant="subtle" className="p-4 text-sm text-ink-muted">
        {t('pallets.noHistory')}
      </Surface>
    );
  }

  return (
    <ol className="flex flex-col gap-2" data-testid="movement-history">
      {rows.map((row) => (
        <li
          key={row.id}
          className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-surface-border px-3 py-2"
        >
          <span className="whitespace-nowrap font-mono text-xs text-ink-muted">
            {formatDateTime(row.at)}
          </span>
          <Badge tone="neutral">{t(`movements.types.${row.type}`)}</Badge>
          <span className="min-w-0 flex-1 truncate text-sm text-ink">
            <span className="font-mono text-xs text-ink-muted">{row.articleNo}</span> {row.articleName}
            {row.lotNo ? <span className="text-ink-muted"> · {row.lotNo}</span> : null}
          </span>
          <span className="whitespace-nowrap font-mono text-xs text-ink-light">
            {row.fromBinCode ?? t('movements.external')} → {row.toBinCode ?? t('movements.external')}
          </span>
          <span className="whitespace-nowrap font-mono text-sm text-ink">{formatQty(row.qtyBase)}</span>
          {row.userName ? <span className="text-xs text-ink-muted">{row.userName}</span> : null}
        </li>
      ))}
    </ol>
  );
}
